import React, { useEffect, useState } from "react";
import { useRoomTasks } from "../../../../context/RoomTasksContext";
import { useRoom } from "../../../../context/RoomContext";
import { motion } from "framer-motion";
import {
  FiCheckCircle,
  FiClock,
  FiAlertTriangle,
  FiTrendingUp,
  FiUsers,
  FiTarget,
  FiActivity,
  FiAward,
  FiLoader,
  FiBarChart2,
} from "react-icons/fi";
import "./TaskStatistics.css";

const TaskStatistics = ({ roomId }) => {
  const { fetchTaskStats } = useRoomTasks();
  const { currentRoom } = useRoom();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const response = await fetchTaskStats(roomId);
        if (response?.data) {
          setStats(response.data);
        }
      } catch (error) { /* ignored */ } finally {
        setLoading(false);
      }
    };

    if (roomId) {
      loadStats();
    }
  }, [roomId, fetchTaskStats]);

  if (loading) {
    return (
      <div className="rts-loading">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <FiLoader size={32} />
        </motion.div>
        <p>Loading statistics...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="rts-empty">
        <FiBarChart2 size={48} />
        <h3>No statistics available</h3>
        <p>Create some tasks to see insights</p>
      </div>
    );
  }

  // Calculate metrics
  const totalTasks = stats.total || 0;
  const byStatus = stats.byStatus || {};
  const completedTasks = byStatus.completed?.count || 0;
  const inProgressTasks = byStatus["in-progress"]?.count || 0;
  const todoTasks = byStatus.todo?.count || 0;
  const overdueTasks = byStatus.overdue?.count || 0;
  const blockedTasks = byStatus.blocked?.count || 0;
  const reviewTasks = byStatus.review?.count || 0;

  const completionRate =
    totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const avgDaysToComplete = stats.avgCompletionTime || 0;
  const highPriorityTasks = stats.highPriority || 0;
  const tasksDueSoon = stats.tasksDueSoon || 0;

  // Member stats
  const allRoomMembers = currentRoom?.members || [];
  const leader = currentRoom?.leader;
  const backendMemberStats = stats.memberStats || [];

  const memberStats = [];

  if (leader) {
    const leaderId = leader._id || leader.id || leader;
    const leaderStats = backendMemberStats.find(
      (m) => m.userId === leaderId.toString()
    );
    memberStats.push({
      userId: leaderId,
      name: leader.name || "Leader",
      profilePhotoUrl: leader.profilePhotoUrl || null,
      totalTasks: leaderStats?.totalTasks || 0,
      completed: leaderStats?.completed || 0,
      inProgress: leaderStats?.inProgress || 0,
      overdue: leaderStats?.overdue || 0,
      completionRate: leaderStats?.completionRate || 0,
    });
  }

  allRoomMembers.forEach((member) => {
    const user = member.user || member;
    const userId = user._id || user.id || user;
    if (
      leader &&
      (leader._id || leader.id || leader).toString() === userId.toString()
    )
      return;

    const memberTaskStats = backendMemberStats.find(
      (m) => m.userId === userId.toString()
    );
    memberStats.push({
      userId,
      name: user.name || "Member",
      profilePhotoUrl: user.profilePhotoUrl || null,
      totalTasks: memberTaskStats?.totalTasks || 0,
      completed: memberTaskStats?.completed || 0,
      inProgress: memberTaskStats?.inProgress || 0,
      overdue: memberTaskStats?.overdue || 0,
      completionRate: memberTaskStats?.completionRate || 0,
    });
  });

  // Status distribution data
  const statusData = [
    { label: "To Do", value: todoTasks, color: "#6366f1" },
    { label: "In Progress", value: inProgressTasks, color: "#f59e0b" },
    { label: "Review", value: reviewTasks, color: "#8b5cf6" },
    { label: "Completed", value: completedTasks, color: "#10b981" },
    { label: "Blocked", value: blockedTasks, color: "#ef4444" },
  ];

  return (
    <div className="rts-container">
      {/* Header */}
      <div className="rts-header">
        <h2 className="rts-title">Statistics</h2>
        <p className="rts-subtitle">Overview of your team's performance</p>
      </div>

      {/* Key Metrics Grid */}
      <div className="rts-metrics-grid">
        <motion.div
          className="rts-metric-card rts-metric-card--primary"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="rts-metric-icon">
            <FiTarget size={22} />
          </div>
          <div className="rts-metric-content">
            <span className="rts-metric-value">{totalTasks}</span>
            <span className="rts-metric-label">Total Tasks</span>
          </div>
        </motion.div>

        <motion.div
          className="rts-metric-card rts-metric-card--success"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <div className="rts-metric-icon">
            <FiCheckCircle size={22} />
          </div>
          <div className="rts-metric-content">
            <span className="rts-metric-value">{completionRate}%</span>
            <span className="rts-metric-label">Completion Rate</span>
          </div>
          <div className="rts-metric-subtext">{completedTasks} completed</div>
        </motion.div>

        <motion.div
          className={`rts-metric-card ${
            overdueTasks > 0
              ? "rts-metric-card--danger"
              : "rts-metric-card--success"
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="rts-metric-icon">
            <FiAlertTriangle size={22} />
          </div>
          <div className="rts-metric-content">
            <span className="rts-metric-value">{overdueTasks}</span>
            <span className="rts-metric-label">Overdue</span>
          </div>
          <div className="rts-metric-subtext">
            {overdueTasks === 0 ? "All on track" : "Needs attention"}
          </div>
        </motion.div>

        <motion.div
          className="rts-metric-card rts-metric-card--info"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <div className="rts-metric-icon">
            <FiClock size={22} />
          </div>
          <div className="rts-metric-content">
            <span className="rts-metric-value">{avgDaysToComplete}</span>
            <span className="rts-metric-label">Avg. Days</span>
          </div>
          <div className="rts-metric-subtext">To complete</div>
        </motion.div>
      </div>

      {/* Status Distribution */}
      <motion.div
        className="rts-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h3 className="rts-section-title">
          <FiActivity size={18} />
          Status Distribution
        </h3>
        <div className="rts-status-bars">
          {statusData.map((status, idx) => {
            const percentage =
              totalTasks > 0 ? (status.value / totalTasks) * 100 : 0;
            return (
              <div key={status.label} className="rts-status-item">
                <div className="rts-status-header">
                  <div className="rts-status-label">
                    <span
                      className="rts-status-dot"
                      style={{ background: status.color }}
                    />
                    {status.label}
                  </div>
                  <span className="rts-status-count">{status.value}</span>
                </div>
                <div className="rts-status-bar">
                  <motion.div
                    className="rts-status-fill"
                    style={{ background: status.color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ duration: 0.8, delay: 0.4 + idx * 0.1 }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Quick Insights */}
      <motion.div
        className="rts-insights"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <div className="rts-insight-card">
          <FiTrendingUp className="rts-insight-icon" />
          <div className="rts-insight-content">
            <span className="rts-insight-value">{highPriorityTasks}</span>
            <span className="rts-insight-label">High Priority</span>
          </div>
        </div>
        <div className="rts-insight-card">
          <FiClock className="rts-insight-icon" />
          <div className="rts-insight-content">
            <span className="rts-insight-value">{tasksDueSoon}</span>
            <span className="rts-insight-label">Due Soon</span>
          </div>
        </div>
        <div className="rts-insight-card">
          <FiActivity className="rts-insight-icon" />
          <div className="rts-insight-content">
            <span className="rts-insight-value">{inProgressTasks}</span>
            <span className="rts-insight-label">In Progress</span>
          </div>
        </div>
        <div className="rts-insight-card">
          <FiAward className="rts-insight-icon" />
          <div className="rts-insight-content">
            <span className="rts-insight-value">{blockedTasks}</span>
            <span className="rts-insight-label">Blocked</span>
          </div>
        </div>
      </motion.div>

      {/* Team Performance */}
      <motion.div
        className="rts-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <h3 className="rts-section-title">
          <FiUsers size={18} />
          Team Performance
        </h3>
        <div className="rts-team-list">
          {memberStats.map((member, idx) => (
            <motion.div
              key={member.userId}
              className="rts-member-card"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 + idx * 0.05 }}
            >
              <div className="rts-member-info">
                {member.profilePhotoUrl ? (
                  <img
                    src={member.profilePhotoUrl}
                    alt={member.name}
                    className="rts-member-avatar"
                  />
                ) : (
                  <div className="rts-member-avatar rts-member-avatar--placeholder">
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="rts-member-details">
                  <span className="rts-member-name">{member.name}</span>
                  <span className="rts-member-tasks">
                    {member.totalTasks} tasks • {member.completed} done
                  </span>
                </div>
              </div>
              <div className="rts-member-progress">
                <div className="rts-progress-ring">
                  <svg viewBox="0 0 36 36">
                    <path
                      className="rts-ring-bg"
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <motion.path
                      className="rts-ring-fill"
                      strokeDasharray={`${member.completionRate}, 100`}
                      d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                      initial={{ strokeDasharray: "0, 100" }}
                      animate={{
                        strokeDasharray: `${member.completionRate}, 100`,
                      }}
                      transition={{ duration: 1, delay: 0.8 + idx * 0.05 }}
                    />
                  </svg>
                  <span className="rts-ring-value">
                    {member.completionRate}%
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default TaskStatistics;
